import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, ExternalLink, Save, Eye } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { siteUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Eyebrow } from "@/components/sg/Brand";
import { MilestoneTimeline } from "@/components/workspace/MilestoneTimeline";

function ViewedChip({ project }) {
  const { user } = useAuth();
  if (!(user?.role === "owner" || user?.plan === "super_premium")) return null;
  if (!project.first_viewed_at) return <div className="text-xs text-muted-foreground mt-1" data-testid="project-viewed-chip">Client hasn't opened the link yet</div>;
  return (
    <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 mt-1" data-testid="project-viewed-chip">
      <Eye className="h-3.5 w-3.5" />Viewed {project.view_count || 1}× · last {fmtDateTime(project.last_viewed_at || project.first_viewed_at)}
    </div>
  );
}

function ProjectHeader({ project, onChange, onDeleted }) {
  const link = `${siteUrl()}/portal/${project.client_token}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Secure client link copied");
    } catch {
      window.prompt("Copy this secure client link:", link);
    }
    await api.post(`/projects/${project.id}/mark-sent`);
    const r = await api.get(`/projects/${project.id}`);
    onChange(r.data);
  };
  const del = async () => {
    if (!window.confirm("Delete this project and its client link? This cannot be undone.")) return;
    await api.delete(`/projects/${project.id}`);
    toast.success("Project deleted");
    onDeleted();
  };
  const collected = project.milestones.filter((m) => m.status === "paid").reduce((a, m) => a + m.amount, 0);
  const pct = project.total_amount ? Math.round((collected / project.total_amount) * 100) : 0;
  return (
    <div className="border border-border rounded-xl bg-card p-6" data-testid="project-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Client</div>
          <h3 className="font-display text-2xl font-bold" data-testid="project-client-name">{project.client_name}</h3>
          <ViewedChip project={project} />
          <div className="text-sm text-muted-foreground mt-2">Total contract value</div>
          <div className="font-display text-3xl font-bold tabular-nums text-indigo-600 dark:text-indigo-400" data-testid="project-total">{money(project.total_amount)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="copy-client-link-btn" onClick={copy} className="bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900">
            <Copy className="h-4 w-4 mr-2" /> Copy Secure Client Link
          </Button>
          <a href={link} target="_blank" rel="noopener noreferrer" data-testid="open-portal-link"><Button variant="outline" size="icon" aria-label="Open client portal"><ExternalLink className="h-4 w-4" /></Button></a>
          <Button data-testid="delete-project-btn" variant="ghost" size="icon" onClick={del} aria-label="Delete project"><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      </div>
      <div className="mt-5">
        <div className="flex justify-between text-xs text-muted-foreground mb-1.5"><span>Collected {money(collected)}</span><span>{pct}%</span></div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
    </div>
  );
}

function ClientContact({ project, onChange }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(project.client_email || "");
  const [phone, setPhone] = useState(project.client_phone || "");
  useEffect(() => { setEmail(project.client_email || ""); setPhone(project.client_phone || ""); }, [project.id, project.client_email, project.client_phone]);
  const patch = async (body, msg) => {
    try { const r = await api.patch(`/projects/${project.id}`, body); onChange(r.data); if (msg) toast.success(msg); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const smsAllowed = user?.plan === "super_premium" || user?.role === "owner";
  const toggles = [
    { key: "reminders_enabled", label: "Automatic reminders", hint: "Email the client 24h and 48h after a request", on: project.reminders_enabled !== false },
    { key: "showcase", label: "Show in portfolio", hint: "Feature this project on your public page (no prices or client names)", on: !!project.showcase },
  ];
  return (
    <div className="border border-border rounded-xl bg-card p-6" data-testid="client-contact-card">
      <Eyebrow className="!text-muted-foreground mb-4">Client contact & automation</Eyebrow>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Client email</Label><Input data-testid="client-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" /></div>
        <div><Label className="text-xs">Client mobile</Label><Input data-testid="client-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="5551234567" /></div>
      </div>
      <Button data-testid="client-contact-save-btn" size="sm" variant="outline" className="mt-3" onClick={() => patch({ client_email: email, client_phone: phone }, "Client contact saved")}>
        <Save className="h-3.5 w-3.5 mr-1.5" />Save contact
      </Button>
      <p className="text-xs text-muted-foreground mt-2">{smsAllowed ? "With a mobile number saved, use “Text client” on any waiting milestone to text from your own phone." : "Super Premium adds one-tap texting from your own phone."}</p>
      <div className="mt-5 divide-y divide-border">
        {toggles.map((t) => (
          <div key={t.key} className="flex items-center justify-between py-3 gap-4">
            <div><div className="text-sm font-medium">{t.label}</div><div className="text-xs text-muted-foreground">{t.hint}</div></div>
            <Switch data-testid={`project-toggle-${t.key}`} checked={t.on} disabled={t.disabled} onCheckedChange={(v) => patch({ [t.key]: v })} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectDetail({ project, onChange, onDeleted }) {
  return (
    <div className="space-y-6">
      <ProjectHeader project={project} onChange={onChange} onDeleted={onDeleted} />
      <MilestoneTimeline project={project} onChange={onChange} />
      <ClientContact project={project} onChange={onChange} />
    </div>
  );
}
